import { Card, CardBody, Container, Stack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import MyBreadcrumb from "components/breadcrumb/Breadcrumb";
import CustomTable from "components/table/CustomTable";
import { useSelector } from "react-redux";
import { Breadcrumb } from "types/Breadcrumb";
import { formatDatetime, truncateString } from "utils/utils";



type ArchivePage = {

};
type TableData = {
  _id: string;
  title: string;
  code: string;
  action: string;
  lastUpdateBy: string;
  originalDocumentId: string;
  createdAt: string;
};
const columnHelper = createColumnHelper<TableData>();

/**
 * ArchivePage component renders the archive page of the admin panel.
 * It displays a table with document modification history.
 *
 * @component
 * @param {ArchivePage} props - The props for the ArchivePage component.
 *
 * @returns {JSX.Element} The rendered ArchivePage component.
 *
 * @example
 * <ArchivePage />
 *
 * @remarks
 * This component uses the following selectors to fetch data from the Redux store:
 * - `state.documentsHistories.documents` for the documents data.
 * - `state.documentsHistories.authors` for the authors data.
 * - `state.documentsHistories.codes` for the codes data.
 * - `state.documentsHistories.actions` for the actions data.
 *
 * The table columns are defined with various configurations, including custom cell rendering,
 * sorting, and filtering options.
 *
 * The breadcrumb navigation is also included to indicate the current page location.
 */
export default function ArchivePage({ }: ArchivePage) {

  const documents = useSelector((state: any) => state.documentsHistories.documents);
  const authors = useSelector((state: any) => state.documentsHistories.authors);
  const codes = useSelector((state: any) => state.documentsHistories.codes);
  const actions = useSelector((state: any) => state.documentsHistories.actions);

  const columns = [
    columnHelper.accessor("title", {
      cell: (info: any) => truncateString(info.getValue(), 80),
      header: `Documento Modificato`,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }),
    columnHelper.accessor("code", {
      cell: (info: any) => truncateString(info.getValue(), 80),
      header: `Codice documento`,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'select',
        placeholder: 'Filtra per codice',
        selectOptions: codes,
      },
    }),
    columnHelper.accessor("action", {
      cell: (info: any) => truncateString(info.getValue()),
      header: `Tipologia modifica`,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'select',
        placeholder: 'Filtra per modifica',
        selectOptions: actions,
      },
    }),
    columnHelper.accessor("lastUpdateBy", {
      cell: (info: any) => truncateString(info.getValue()),
      header: `Autore modifica`,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'select',
        placeholder: 'Filtra per autore',
        selectOptions: authors,
      },
    }),
    columnHelper.accessor("createdAt", {
      cell: (info: any) => formatDatetime(info.getValue()),
      header: `Data modifica`,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }),
    columnHelper.accessor("_id", {
      cell: ({ row }) => (
        <Stack spacing={2} direction="row" align="center">
          {row.original._id}
        </Stack>
      ),
      header: ``,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }),
  ];


  const breadcrumb: Breadcrumb = new Breadcrumb(
    [
      { label: "Archivio", link: "/archive", value: "", isActive: false },
    ]
  );

  return (
    <section className="dashboard-first-section">
      <Container maxW="container.lg" maxH={"container.xl"} textAlign={"left"}>
        <MyBreadcrumb breadcrumb={breadcrumb}/>

        <Card minH={"60vh"}>
          <CardBody>
            <CustomTable data={documents} columns={columns} />
          </CardBody>
        </Card>

      </Container>
    </section>
  );
}